import { ns } from '@leverege/i18n';
import { Checkbox, PropertyGrid, Selector, TextInput } from '@leverege/ui-elements';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelEditorFactory, ModelTypeSelector } from '@leverege/ui-plugin';
import React from 'react';
import Colorizers from "../../colorizer/Colorizers.js";
import Variantizers from "../../variantizer/Variantizers.js";
import Visibilizers from "../../visibilizer/Visibilizers.js";
import S from './DetailEditor.css';
import Model from "./UnitAttributeDetailModel.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = ns('ui-attributes.detail.type.UnitAttributeDetailEditor');
function UnitAttributeDetailEditor(props) {
  const {
    value,
    objectType
  } = props;
  const onModelChange = useValueChange(Model, props);
  if (value == null) {
    return null;
  }
  const formatter = Model.getFormatter(value);
  const formatterEditor = formatter && ModelEditorFactory.create(formatter, {
    eventData: 'setFormatter',
    onChange: onModelChange
  });
  const lFont = ModelEditorFactory.create(Model.getLabelFont(value), {
    eventData: 'setLabelFont',
    onChange: onModelChange
  });
  const vFont = ModelEditorFactory.create(Model.getValueFont(value), {
    eventData: 'setValueFont',
    onChange: onModelChange
  });
  const uFont = ModelEditorFactory.create(Model.getUnitFont(value), {
    eventData: 'setUnitFont',
    onChange: onModelChange
  });
  return /*#__PURE__*/_jsxs(PropertyGrid, {
    style: {
      backgroundColor: 'var(--color-white)'
    },
    children: [formatterEditor && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      inset: true,
      label: t('format'),
      children: formatterEditor
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fillCell: true,
      label: t('showLabel'),
      children: /*#__PURE__*/_jsx(Checkbox, {
        variant: "small",
        value: Model.getLabelVisible(value),
        eventData: "setLabelVisible",
        onChange: onModelChange
      })
    }), Model.getLabelVisible(value) && /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      inset: true,
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        style: {
          backgroundColor: 'var(--color-white)',
          border: 0
        },
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('label'),
          children: /*#__PURE__*/_jsx(TextInput, {
            disabled: !Model.getLabelVisible(value),
            eventData: "setLabel",
            hint: t('labelHint'),
            value: Model.getLabel(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('variant'),
          children: Model.getLabelVisible(value) && ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              ...props,
              objectType
            },
            modelKey: 'variantizer',
            values: Variantizers.getVariantizersFor(objectType),
            value: Model.getLabelVariant(value),
            eventData: 'setLabelVariant',
            onChange: onModelChange
          }, S.fill)
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('color'),
          children: Model.getLabelVisible(value) && ModelTypeSelector.createSelectorAndEditor({
            editorProps: {
              ...props,
              objectType
            },
            modelKey: 'colorizer',
            values: Colorizers.getColorizersFor(objectType),
            value: Model.getLabelColor(value),
            eventData: 'setLabelColor',
            onChange: onModelChange
          }, S.fill)
        }), lFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('font'),
          children: lFont
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('justify'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setLabelJustify",
            value: Model.getLabelJustify(value),
            values: [{
              value: null,
              name: t('none')
            }, {
              value: 'left',
              name: t('left')
            }, {
              value: 'center',
              name: t('center')
            }, {
              value: 'right',
              name: t('right')
            }],
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('align'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setLabelAlign",
            value: Model.getLabelAlign(value),
            values: [{
              value: null,
              name: t('none')
            }, {
              value: 'top',
              name: t('top')
            }, {
              value: 'center',
              name: t('center')
            }, {
              value: 'bottom',
              name: t('bottom')
            }],
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: t('textWrap'),
          children: /*#__PURE__*/_jsx(Checkbox, {
            variant: "small",
            value: Model.getLabelWrap(value),
            eventData: "setLabelWrap",
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      inset: true,
      label: t('value'),
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        style: {
          backgroundColor: 'var(--color-white)',
          border: 0
        },
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('visibilizer'),
          fill: true,
          children: /*#__PURE__*/_jsx("div", {
            children: ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                ...props,
                objectType
              },
              modelKey: 'visibilizer',
              values: Visibilizers.getVisibilizersFor(objectType),
              value: Model.getValueVisible(value),
              eventData: 'setValueVisible',
              onChange: onModelChange
            }, S.fill)
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('variant'),
          fill: true,
          children: /*#__PURE__*/_jsx("div", {
            children: ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                ...props,
                objectType
              },
              modelKey: 'variantizer',
              values: Variantizers.getVariantizersFor(objectType),
              value: Model.getValueVariant(value),
              eventData: 'setValueVariant',
              onChange: onModelChange
            }, S.fill)
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('color'),
          fill: true,
          children: /*#__PURE__*/_jsx("div", {
            children: ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                ...props,
                objectType
              },
              modelKey: 'colorizer',
              values: Colorizers.getColorizersFor(objectType),
              value: Model.getValueColor(value),
              eventData: 'setValueColor',
              onChange: onModelChange
            }, S.fill)
          })
        }), vFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('font'),
          children: vFont
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('justify'),
          children: /*#__PURE__*/_jsx(Selector, {
            value: Model.getValueJustify(value),
            eventData: "setValueJustify",
            values: [{
              value: null,
              name: t('none')
            }, {
              value: 'left',
              name: t('left')
            }, {
              value: 'center',
              name: t('center')
            }, {
              value: 'right',
              name: t('right')
            }],
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('align'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setValueAlign",
            value: Model.getValueAlign(value),
            values: [{
              value: null,
              name: t('none')
            }, {
              value: 'top',
              name: t('top')
            }, {
              value: 'center',
              name: t('center')
            }, {
              value: 'baseline',
              name: t('bottom')
            }],
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: t('textWrap'),
          children: /*#__PURE__*/_jsx(Checkbox, {
            variant: "small",
            value: Model.getValueWrap(value),
            eventData: "setValueWrap",
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      fill: true,
      inset: true,
      label: t('unit'),
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        style: {
          backgroundColor: 'var(--color-white)',
          border: 0
        },
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('color'),
          fill: true,
          children: /*#__PURE__*/_jsx("div", {
            children: Model.getLabelVisible(value) && ModelTypeSelector.createSelectorAndEditor({
              editorProps: {
                ...props,
                objectType
              },
              modelKey: 'colorizer',
              values: Colorizers.getColorizersFor(objectType),
              value: Model.getUnitColor(value),
              eventData: 'setUnitColor',
              onChange: onModelChange
            }, S.fill)
          })
        }), uFont != null && /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('font'),
          children: uFont
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          stretch: true,
          label: t('align'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setUnitAlign",
            value: Model.getUnitAlign(value),
            values: [{
              value: null,
              name: t('none')
            }, {
              value: 'top',
              name: t('top')
            }, {
              value: 'center',
              name: t('center')
            }, {
              value: 'bottom',
              name: t('bottom')
            }],
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
      label: t('background'),
      children: /*#__PURE__*/_jsx(TextInput, {
        eventData: "setBackground",
        hint: t('backgroundHint'),
        value: Model.getBackground(value),
        onChange: onModelChange
      })
    })]
  });
}
export default /*#__PURE__*/React.memo(UnitAttributeDetailEditor);